import React, { FC, useCallback, useEffect, useState } from "react";
import { RouteComponentProps, useNavigate } from "@reach/router";
import { Callout } from "kanopi-components/Callout";
import { PortalRow } from "kanopi-components/PortalRow";
import { HeadingLarge } from "kanopi-components/typography";
import { FlowLayout } from "kw/components/FlowLayout";
import { WidthContainer } from "kw/util/ChatFlow";
import { ClaimState } from "kw/claims/components/ClaimState/ClaimState";
import { useAPI } from "kw/api/useAPI";
import { TTableRow, TwoColumnTable } from "kw/components/TabularDisplay/TwoColumnTable";
import { Placeholder } from "kanopi-components/Placeholder";
import { useGlobal } from "kanopi-components/useGlobal";
import { NavButton } from "kw/components/NavButton/NavButton";
import { ClaimStatusChoices } from "kw/api/zeus";
import { PolicyFixedFooter } from "kw/policies/components/PolicyFixedFooter";

type TClaimSummaryPage = {
  location?: {
    state: {
      history: unknown[];
    };
  };
  policyId?: string;
};
export const ClaimSummaryPage: FC<TClaimSummaryPage & RouteComponentProps> = ({
  policyId,
  ...props
}) => {
  const { query, loading: policyDetailsLoading } = useAPI();
  const { mutation, loading: submitting } = useAPI();
  const [policyDetails, setPolicyDetails] = useState<TTableRow[]>();
  const [data] = useGlobal("claiming");
  const navigate = useNavigate();
  const historyPrefix = props?.location?.pathname?.replace("summary", "details");
  const gotoClaimDetails = useCallback(() => {
    navigate(historyPrefix);
  }, []);

  const submitClaim = useCallback(() => {
    mutation(
      {
        submitClaim: [
          {
            input: {
              claimProfileId: data?.profile?.id,
            },
          },
          {
            status: true,
          },
        ],
      },
      (response) => {
        if (response?.submitClaim[0].status === ClaimStatusChoices.SUBMITTED) {
          navigate(`/claims/make-a-claim/${policyId}/success`);
        }
      }
    );
  }, [data?.profile?.id]);
  useEffect(() => {
    const _history = props?.location?.state?.history;
    const getPolicyDetails = () => {
      query(
        {
          policy: [
            {
              id: policyId,
            },
            {
              policyNumber: true,
              startDate: true,
              finishDate: true,
              quote: {
                product: {
                  name: true,
                },
                userReference: true,
              },
            },
          ],
        },
        (response: { policy: TPolicy }) => {
          const { policy: _policy } = response;
          const rows: TTableRow[] = [
            {
              label: "Policy type",
              value: _policy?.quote?.product?.name,
            },
            {
              label: "Policy number",
              value: _policy?.policyNumber,
            },
            {
              label: "Policy start date",
              value: _policy?.startDate,
            },
            {
              label: "Policy finish date",
              value: _policy?.finishDate,
            },
            {
              label: "Insured asset",
              value: _policy?.quote?.userReference,
            },
          ];
          setPolicyDetails(rows);
        }
      );
    };
    if (_history && _history?.length > 0 && data?.profile?.id) {
      getPolicyDetails();
    } else {
      navigate(`/claims/make-a-claim/${policyId}/details`);
    }
  }, []);
  return (
    <FlowLayout
      steps={[
        {
          label: "Your claim details",
          current: 1,
          total: 1,
        },
        { label: "Review and submit", active: true },
      ]}
      history={props?.location?.state?.history || []}
      prefix={historyPrefix}
    >
      <WidthContainer>
        <PortalRow>
          <HeadingLarge margin={0}>Ready to submit</HeadingLarge>
        </PortalRow>
        <PortalRow>
          <Callout>
            Please review your claim before submitting it. Once submitted, we’ll be in
            touch via email and this portal to keep you informed
          </Callout>
        </PortalRow>
        <PortalRow>
          <ClaimState
            claimProfileId={data?.profile?.id}
            disabled={false}
            buttonText="Edit"
            update={gotoClaimDetails}
          />
        </PortalRow>
        <PortalRow>
          <HeadingLarge margin={0}>Policy to claim on</HeadingLarge>
        </PortalRow>
        <Placeholder loading={policyDetailsLoading}>
          <PortalRow>
            <TwoColumnTable id="claimState-PolicyDetails" rows={policyDetails} />
          </PortalRow>
        </Placeholder>
        <PolicyFixedFooter>
          <NavButton
            onClick={submitClaim}
            isLoading={submitting}
            data-testid="submitClaimCta"
          >
            Submit your claim
          </NavButton>
        </PolicyFixedFooter>
      </WidthContainer>
    </FlowLayout>
  );
};
